import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { API_URL, errorDetail, fetchJson } from "@/lib/admin-api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Which unit sits behind a device row, and the two actions that used to be SQL (POS2-1354 CP3).
 *
 * Revoking is a rotation for an enrolled unit, which drops the refused token and enrols again by
 * itself (POS2-1385). A token provisioned by hand (`DEVICE_TOKEN`, no `balena_uuid`) cannot be
 * re-minted by the device, so revoking it silences the device. Retiring soft-deletes the row:
 * auth and enrolment both refuse it, so no unit can take it over again.
 */

interface Enrolment {
  balena_uuid: string | null;
  live_tokens: number;
  last_enrolled_at: string | null;
}

type Action = "revoke-tokens" | "retire";

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

export function DeviceEnrolment({
  deviceId,
  serial,
  retired,
}: {
  deviceId: string;
  serial: string;
  /** Read-only: a retired device's tokens are shown, and nothing can be done to it. */
  retired: boolean;
}) {
  const headers = useApiHeaders();
  const [action, setAction] = useState<Action | null>(null);
  const enrolment = useQuery<Enrolment, Error>({
    queryKey: ["admin", "devices", deviceId, "enrolment"],
    queryFn: () =>
      fetchJson<Enrolment>(`${API_URL}/api/v1/admin/devices/${deviceId}/enrolment`, headers),
    retry: false,
  });

  const data = enrolment.data;
  const byHand = !!data && data.live_tokens > 0 && data.balena_uuid === null;

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">Enrolment</p>
      {enrolment.isError && <p className="text-red-700">{errorDetail(enrolment.error)}</p>}
      {data && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <dt className="text-gray-400">Balena device</dt>
          <dd className="break-all text-gray-900">
            {data.balena_uuid ? (
              <a
                href={`https://dashboard.balena-cloud.com/devices/${data.balena_uuid}/summary`}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
              >
                {data.balena_uuid}
              </a>
            ) : (
              "—"
            )}
          </dd>
          <dt className="text-gray-400">Live tokens</dt>
          <dd className="text-gray-900">
            {data.live_tokens}
            {byHand && <span className="ml-2 text-amber-700">provisioned by hand</span>}
          </dd>
          <dt className="text-gray-400">Last enrolled</dt>
          <dd className="text-gray-900">{formatTime(data.last_enrolled_at)}</dd>
        </dl>
      )}
      {!retired && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={!data || data.live_tokens === 0}
            onClick={() => setAction("revoke-tokens")}
          >
            Revoke tokens
          </Button>
          <Button variant="destructive" disabled={!data} onClick={() => setAction("retire")}>
            Retire device
          </Button>
        </div>
      )}
      {action && data && (
        <ActionDialog
          deviceId={deviceId}
          serial={serial}
          action={action}
          enrolled={data.balena_uuid !== null}
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}

function ActionDialog({
  deviceId,
  serial,
  action,
  enrolled,
  onClose,
}: {
  deviceId: string;
  serial: string;
  action: Action;
  enrolled: boolean;
  onClose: () => void;
}) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const run = useMutation<unknown, Error, void>({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/devices/${deviceId}/${action}`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "devices"] });
      onClose();
    },
  });

  const retire = action === "retire";
  const title = retire ? `Retire ${serial}` : `Revoke tokens of ${serial}`;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {retire
              ? "Soft-deletes the device and revokes its tokens. Its history stays."
              : "For a token you suspect has leaked."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-gray-600">
          {retire ? (
            <p>
              From then on the device is refused (403), and no unit can enrol as {serial} again
              (409). It stays on this page, listed last. Use it for a row that is history only, so a
              reflashed unit cannot take it over.
            </p>
          ) : !enrolled ? (
            <p role="alert" className="font-medium text-red-700">
              This device has no balena device on record, so its token was provisioned by hand. It
              will stop sending until someone gives it a new DEVICE_TOKEN.
            </p>
          ) : (
            <p>The device drops the refused token and enrols again by itself within a minute.</p>
          )}
          {run.isError && <p className="text-red-700">{errorDetail(run.error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={retire ? "destructive" : "default"}
            onClick={() => run.mutate()}
            disabled={run.isPending}
          >
            {retire ? "Retire device" : "Revoke tokens"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
